import { useAuth } from "../App";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Sparkles, Palette, Download, Printer, Star, ArrowRight, Wand2, ZoomIn } from "lucide-react";
import { useState } from "react";

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);

  const handleGetStarted = () => {
    if (user) {
      navigate("/generate");
    } else {
      navigate("/signup");
    }
  };

  const samplePages = [
    { src: "/samples/page-1.png", title: "Friendly Lion", prompt: "Cute jungle lion" },
    { src: "/samples/page-2.png", title: "Playful Lion Cub", prompt: "Happy lion cub playing" },
    { src: "/samples/page-3.png", title: "Jungle Friends", prompt: "Jungle animal friends" },
    { src: "/samples/page-4.png", title: "Safari Adventure", prompt: "Safari adventure scene" },
  ];

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 font-medium" data-testid="nav-pricing">
                Pricing
              </Link>
              <Link to="/free-tools" className="text-slate-600 hover:text-slate-900 font-medium" data-testid="nav-free-tools">
                Free Tools
              </Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 font-medium" data-testid="nav-blog">
                Blog
              </Link>
              {user ? (
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:shadow-lg btn-lift"
                  data-testid="nav-dashboard"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Link to="/login" className="text-slate-600 hover:text-slate-900 font-medium" data-testid="nav-login-link">
                    Login
                  </Link>
                  <Button
                    onClick={() => navigate("/signup")}
                    className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:shadow-lg btn-lift"
                    data-testid="nav-signup"
                  >
                    Sign Up Free
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full text-violet-700 font-medium text-sm mb-6">
                <Sparkles className="w-4 h-4" />
                AI-Powered Coloring Books
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold text-slate-800 leading-tight mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Create Magical
                <span className="bg-gradient-to-r from-violet-600 to-pink-500 bg-clip-text text-transparent"> Coloring Books</span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-lg">
                Transform any idea into beautiful, printable coloring pages. 
                Perfect for kids, teachers, and creative minds of all ages.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleGetStarted}
                  className="h-14 px-8 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold text-lg hover:shadow-xl btn-lift"
                  data-testid="hero-cta"
                >
                  <Wand2 className="w-5 h-5 mr-2" />
                  Create Your Book
                </Button>
                <Link to="/pricing">
                  <Button
                    variant="outline"
                    className="h-14 px-8 rounded-full border-2 border-slate-200 text-slate-700 font-semibold text-lg hover:bg-slate-50"
                    data-testid="hero-pricing"
                  >
                    View Pricing
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>

              <div className="flex items-center gap-6 mt-8">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-200 to-pink-200 border-2 border-white"
                    />
                  ))}
                </div>
                <div className="text-sm text-slate-600">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <span>Loved by 10,000+ families</span>
                </div>
              </div>
            </div>

            <div className="relative animate-slide-up stagger-2">
              <div className="absolute -top-4 -left-4 w-72 h-72 bg-violet-200 rounded-full opacity-30 blur-3xl"></div>
              <div className="absolute -bottom-4 -right-4 w-72 h-72 bg-pink-200 rounded-full opacity-30 blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1758598737505-90a3084105ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNoaWxkJTIwY29sb3JpbmclMjBkcmF3aW5nfGVufDB8fHx8MTc2NTkwMTg4NXww&ixlib=rb-4.1.0&q=85&w=800"
                alt="Child coloring"
                className="relative rounded-3xl shadow-2xl w-full object-cover aspect-square"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Create professional coloring books in minutes, not hours
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Wand2,
                title: "Describe Your Theme",
                description: "Tell us what you want - cute animals, superheroes, princesses, dinosaurs, or anything else!"
              },
              {
                icon: Sparkles,
                title: "AI Creates 10 Pages",
                description: "Our AI generates unique, high-quality line art perfect for coloring"
              },
              {
                icon: Download,
                title: "Download & Print",
                description: "Get your coloring book as a print-ready PDF in A4 format"
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-card-gradient rounded-3xl p-8 border border-slate-100 card-hover"
              >
                <div className="feature-icon mb-6">
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Gallery Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full text-violet-700 font-medium text-sm mb-6">
              <Star className="w-4 h-4 fill-violet-500" />
              Real AI-Generated Samples
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              See What You Can Create
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Stunning, print-ready coloring pages generated by our AI in seconds
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {samplePages.map((page, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                onClick={() => setSelectedImage(page)}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={page.src}
                    alt={page.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                </div>
                {/* Prompt text below image */}
                <div className="p-3 bg-white">
                  <p className="text-xs text-violet-600 font-medium">Prompt:</p>
                  <p className="text-sm text-slate-700 italic">"{page.prompt}"</p>
                </div>
                {/* Corner badge */}
                <div className="absolute top-3 right-3 bg-violet-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                  AI
                </div>
              </div>
            ))}
          </div>

          {/* Sample Book Info */}
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  "Jungle Animals" Coloring Book
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  This adorable jungle-themed coloring book was created with just one prompt: 
                  <span className="italic text-violet-600 font-medium"> "cute jungle animals for kids"</span>. 
                  Our AI generated 7 unique pages featuring lions, elephants, monkeys, and more!
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  {["Lion", "Elephant", "Monkey", "Giraffe", "Tiger", "Toucan"].map((animal) => (
                    <span key={animal} className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
                      {animal}
                    </span>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={handleGetStarted}
                    className="h-12 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:shadow-lg btn-lift"
                  >
                    <Wand2 className="w-5 h-5 mr-2" />
                    Create Your Own
                  </Button>
                  <a 
                    href="/samples/sample_book.pdf" 
                    download="ColorWonder_Jungle_Animals.pdf"
                    className="inline-flex items-center justify-center h-12 px-6 rounded-full border-2 border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition-colors"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Sample PDF
                  </a>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -top-4 -right-4 w-32 h-32 bg-violet-200 rounded-full opacity-50 blur-2xl"></div>
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-pink-200 rounded-full opacity-50 blur-2xl"></div>
                <div className="relative grid grid-cols-2 gap-3">
                  {samplePages.slice(0, 4).map((page, index) => (
                    <div 
                      key={index}
                      className={`rounded-xl overflow-hidden shadow-lg transform transition-transform hover:scale-105 ${
                        index === 0 ? 'rotate-[-3deg]' : 
                        index === 1 ? 'rotate-[2deg]' : 
                        index === 2 ? 'rotate-[3deg]' : 'rotate-[-2deg]'
                      }`}
                    >
                      <img
                        src={page.src}
                        alt={page.title}
                        className="w-full h-auto"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] animate-scale-in">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-violet-300 transition-colors"
            >
              <span className="text-lg font-medium">Close ✕</span>
            </button>
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl"
            />
            <p className="text-center text-white mt-4 font-semibold text-lg">{selectedImage.title}</p>
          </div>
        </div>
      )}

      {/* Testimonials Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              What Our Beta Testers Say
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Early feedback from creators who helped shape ColorWonder
            </p>
          </div>

          {/* Testimonials Grid - 2 Beta Testers */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Testimonial 1 - Mitali Sinha */}
            <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-2xl p-8 border border-violet-100">
              <div className="flex text-yellow-400 text-lg mb-4">★★★★★</div>
              <p className="text-slate-700 leading-relaxed text-lg mb-6 italic">
                "ColorWonder made creating coloring pages so effortless. I generated an entire themed book for my niece's birthday in under an hour!"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                  MS
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">Mitali Sinha</h4>
                  <p className="text-sm text-violet-600 font-medium">Beta Tester</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 - Eshaan Srivastava */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
              <div className="flex text-yellow-400 text-lg mb-4">★★★★★</div>
              <p className="text-slate-700 leading-relaxed text-lg mb-6 italic">
                "The AI quality blew me away. Clean lines, perfect for printing. This tool is a must-have for parents and teachers!"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold text-lg">
                  ES
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">Eshaan Srivastava</h4>
                  <p className="text-sm text-blue-600 font-medium">Beta Tester</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Example Themes */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-hero-gradient">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Endless Possibilities
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From cute animals to mythological creatures - create any theme you can imagine
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "Cute Animals",
              "Superheroes",
              "Princesses",
              "Dinosaurs",
              "Indian Mythology",
              "Space Adventures",
              "Under the Sea",
              "Fantasy Creatures"
            ].map((theme, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 text-center border border-slate-100 card-hover cursor-pointer"
                onClick={handleGetStarted}
              >
                <span className="text-slate-700 font-medium">{theme}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-violet-600 to-indigo-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Ready to Create Magic?
          </h2>
          <p className="text-xl text-violet-100 mb-8 max-w-2xl mx-auto">
            Start with 5 free pages and create your first coloring book today
          </p>
          <Button
            onClick={handleGetStarted}
            className="h-14 px-10 rounded-full bg-white text-violet-700 font-semibold text-lg hover:bg-violet-50 hover:shadow-xl btn-lift"
            data-testid="cta-button"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            Get Started Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 md:px-12 lg:px-24 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                  <Palette className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold text-xl text-white" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  ColorWonder
                </span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                AI-powered coloring book generator for creative families and educators.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/pricing" className="text-slate-400 hover:text-white text-sm transition-colors">Pricing</Link></li>
                <li><Link to="/free-tools" className="text-slate-400 hover:text-white text-sm transition-colors">Free Tools</Link></li>
                <li><Link to="/faq" className="text-slate-400 hover:text-white text-sm transition-colors">FAQ</Link></li>
                <li><Link to="/about" className="text-slate-400 hover:text-white text-sm transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">Terms of Service</Link></li>
                <li><Link to="/refund" className="text-slate-400 hover:text-white text-sm transition-colors">Refund Policy</Link></li>
                <li><Link to="/cookies" className="text-slate-400 hover:text-white text-sm transition-colors">Cookie Policy</Link></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><a href="mailto:support@colorwonder.app" className="text-slate-400 hover:text-white text-sm transition-colors">support@colorwonder.app</a></li>
                <li><Link to="/faq" className="text-slate-400 hover:text-white text-sm transition-colors">Help Center</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © 2025 ColorWonder AI. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">Privacy</Link>
              <Link to="/terms" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">Terms</Link>
              <Link to="/cookies" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
